/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { TrendingUp, Award, Target, Calendar } from 'lucide-react';

interface EarTrainingProgressProps {
  currentKey: string;
  currentScale: string;
}

const EarTrainingProgress: React.FC<EarTrainingProgressProps> = ({
  currentKey,
  currentScale
}) => {
  // Mock progress data (in a real app, this would come from localStorage or a backend)
  const progressData = {
    sessionsCompleted: 23,
    totalQuestions: 247,
    correctAnswers: 189,
    currentStreak: 7,
    bestStreak: 15,
    intervalsLearned: ['P8', 'P5', 'P4', 'M3', 'm3'],
    weakAreas: ['m2', 'M7', 'TRI'],
    recentSessions: [
      { date: '2024-01-15', accuracy: 85, questions: 12 },
      { date: '2024-01-14', accuracy: 78, questions: 15 },
      { date: '2024-01-13', accuracy: 92, questions: 8 },
      { date: '2024-01-12', accuracy: 73, questions: 10 }
    ],
    achievements: [
      { name: 'First Steps', description: 'Complete your first ear training session', earned: true },
      { name: 'Perfect Pitch', description: 'Get 10 questions correct in a row', earned: true },
      { name: 'Interval Master', description: 'Achieve 90% accuracy over 50 questions', earned: false },
      { name: 'Dedicated Learner', description: 'Practice for 7 consecutive days', earned: true }
    ]
  };

  const overallAccuracy = Math.round((progressData.correctAnswers / progressData.totalQuestions) * 100);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Your Progress
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{overallAccuracy}%</div>
              <div className="text-sm text-muted-foreground">Overall Accuracy</div>
              <div className="text-xs text-muted-foreground mt-1">
                {progressData.correctAnswers}/{progressData.totalQuestions} questions
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{progressData.currentStreak}</div>
              <div className="text-sm text-muted-foreground">Current Streak</div>
              <div className="text-xs text-muted-foreground mt-1">
                Best: {progressData.bestStreak} questions
              </div>
            </div>
          </Card>
        </div>

        {/* Sessions Completed */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4" />
            <h4 className="font-semibold">Sessions Completed</h4>
          </div>
          <div className="text-2xl font-bold">{progressData.sessionsCompleted}</div>
          <div className="text-sm text-muted-foreground">
            Keep up the great work!
          </div>
        </Card>

        {/* Intervals Progress */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-4 w-4" />
            <h4 className="font-semibold">Interval Mastery</h4>
          </div>
          
          <div className="space-y-3">
            <div>
              <h5 className="text-sm font-medium text-green-700 mb-2">✓ Learned</h5>
              <div className="flex flex-wrap gap-1">
                {progressData.intervalsLearned.map((interval) => (
                  <Badge key={interval} variant="default" className="text-xs">
                    {interval}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div>
              <h5 className="text-sm font-medium text-amber-700 mb-2">📚 Need Practice</h5>
              <div className="flex flex-wrap gap-1">
                {progressData.weakAreas.map((interval) => (
                  <Badge key={interval} variant="secondary" className="text-xs">
                    {interval}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Recent Sessions */}
        <Card className="p-4">
          <h4 className="font-semibold mb-3">Recent Performance</h4>
          <div className="space-y-2">
            {progressData.recentSessions.map((session, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="text-sm">
                  <div className="font-medium">{session.date}</div>
                  <div className="text-xs text-muted-foreground">
                    {session.questions} questions
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-16">
                    <Progress value={session.accuracy} className="h-2" />
                  </div>
                  <Badge 
                    variant={session.accuracy >= 80 ? "default" : "secondary"}
                    className="text-xs"
                  >
                    {session.accuracy}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Achievements */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Award className="h-4 w-4" />
            <h4 className="font-semibold">Achievements</h4>
          </div>
          <div className="space-y-2">
            {progressData.achievements.map((achievement, index) => (
              <div 
                key={index} 
                className={`flex items-center gap-3 p-2 rounded-lg ${
                  achievement.earned ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
                }`}
              >
                <div className={`text-lg ${achievement.earned ? '🏆' : '🔒'}`}>
                  {achievement.earned ? '🏆' : '🔒'}
                </div>
                <div className="flex-1">
                  <div className={`font-medium text-sm ${
                    achievement.earned ? 'text-yellow-800' : 'text-gray-600'
                  }`}>
                    {achievement.name}
                  </div>
                  <div className={`text-xs ${
                    achievement.earned ? 'text-yellow-600' : 'text-gray-500'
                  }`}>
                    {achievement.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Motivational Message */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="text-center space-y-2">
            <h4 className="font-semibold text-blue-900">Keep Going! 🎵</h4>
            <p className="text-sm text-blue-800">
              {overallAccuracy >= 90 && "Incredible! You're mastering ear training."}
              {overallAccuracy >= 80 && overallAccuracy < 90 && "Excellent progress! You're getting really good."}
              {overallAccuracy >= 70 && overallAccuracy < 80 && "Great work! Keep practicing to improve further."}
              {overallAccuracy < 70 && "Every expert was once a beginner. Keep practicing!"}
            </p>
            <p className="text-xs text-blue-600">
              Try practicing the intervals you find challenging in Learn mode.
            </p>
          </div>
        </Card>

        {/* Goals */}
        <Card className="p-4">
          <h4 className="font-semibold mb-3">Suggested Goals</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">Practice weak intervals (m2, M7)</span>
              <Badge variant="outline" className="text-xs">Recommended</Badge>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">Achieve 90% accuracy</span>
              <Badge variant="outline" className="text-xs">
                {Math.max(0, 90 - overallAccuracy)}% to go
              </Badge>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">Complete 30 sessions</span>
              <Badge variant="outline" className="text-xs">
                {Math.max(0, 30 - progressData.sessionsCompleted)} left
              </Badge>
            </div>
          </div>
        </Card>
      </CardContent>
    </Card>
  );
};

export default EarTrainingProgress;